/**
 * apiCacheInterceptor — iter-156cb.
 *
 * Wires axios so that any successful non-GET request to a tracked
 * endpoint automatically wipes the matching cache prefix. Removes
 * the need to call `invalidate('/api/jobs')` at every mutation
 * site (and the bugs that come from forgetting).
 *
 * Tracked roots (URL prefixes):
 *   /api/jobs       → invalidate all /api/jobs* cache entries
 *   /api/team       → invalidate all /api/team* cache entries
 *   /api/crews      → invalidate all /api/crews* cache entries
 *
 * The interceptor watches RESPONSES (not requests) so we don't
 * invalidate the cache if the write fails (the cached data is
 * still accurate in that case).
 *
 * Activation: imported once at app boot (App.js / index.js). The
 * interceptor lives on the default axios instance; every component
 * already uses the default instance.
 */
import axios from 'axios';
import { invalidatePrefix } from './apiCache';
import { apiClient } from '@/api/client';

// Which endpoint prefixes to track. Keep the list short and
// explicit — over-invalidating defeats the cache.
const TRACKED_PREFIXES = [
  '/api/jobs',
  '/api/team',
  '/api/crews',
];

// Mutations that should trigger invalidation. GET reads are
// intentionally excluded — those POPULATE the cache, they don't
// invalidate it.
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

let _installed = false;
/**
 * Internal: the response interceptor handler. Exported so tests
 * can drive it directly without needing to mock axios deeply.
 *
 * iter-156fa — Handles two URL shapes because the interceptor is
 * installed on both the global `axios` (full URLs) AND the
 * `apiClient` instance (which has `baseURL: ${BACKEND}/api`, so
 * config.url is relative — e.g. `/jobs/{id}` instead of
 * `https://.../api/jobs/{id}`). Without normalization here, the
 * apiClient mutations slipped through the prefix match and the
 * jobs-list cache went stale-after-update — symptom: the inline
 * status dropdown on a job card would flip back to "Scheduled" a
 * few seconds after the user changed it (next /api/jobs read
 * served a 60-second-old cached payload).
 */
export function _cacheInterceptorHandler(response) {
  try {
    const method = String(response?.config?.method || '').toUpperCase();
    if (!MUTATING_METHODS.has(method)) return response;
    const rawUrl = response?.config?.url || '';
    const baseURL = response?.config?.baseURL || '';
    let path;
    try {
      path = new URL(rawUrl, (typeof window !== 'undefined' && window.location) ? window.location.origin : 'http://localhost').pathname;
    } catch {
      path = rawUrl;
    }
    // apiClient sets baseURL = `${BACKEND}/api`, so config.url is
    // relative (e.g. `/jobs/{id}`). Reconstruct the full path with
    // the `/api` prefix that TRACKED_PREFIXES expects.
    if (!path.startsWith('/api') && baseURL) {
      try {
        const bPath = new URL(baseURL, 'http://localhost').pathname.replace(/\/+$/, '');
        if (bPath && !path.startsWith(bPath)) {
          path = bPath + (path.startsWith('/') ? path : '/' + path);
        }
      } catch { /* fall through with the original path */ }
    }
    for (const root of TRACKED_PREFIXES) {
      if (path.startsWith(root)) {
        invalidatePrefix(root);
      }
    }
  } catch {
    // Cache invalidation must NEVER break a real response.
  }
  return response;
}

export function installApiCacheInterceptor() {
  if (_installed) return;
  _installed = true;
  axios.interceptors.response.use(
    _cacheInterceptorHandler,
    (error) => Promise.reject(error),
  );
  // iter-156fa — Also install on the `apiClient` axios instance.
  // Mutations from `api/jobs.js`, `api/team.js`, etc. flow through
  // `apiClient` (a separate axios.create() instance), so they
  // bypassed the global interceptor and never invalidated the
  // matching prefix cache. See `_cacheInterceptorHandler` for the
  // relative-URL handling required by this instance.
  apiClient.interceptors.response.use(
    _cacheInterceptorHandler,
    (error) => Promise.reject(error),
  );
}
